import { DatePicker, Input, Select, type Column } from "@legal-500/legal500-design-system";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { auditKeys, auditService, type AuditEntry } from "@/api/profiles";
import { DataTable } from "@/components/DataTable/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls";
import { useCompanyNames } from "@/components/Profiles/ListFilters";
import styles from "./Audit.module.css";
import {
  ACTION_OPTIONS,
  ActionPill,
  ActorCell,
  ENTITY_TYPE_OPTIONS,
  EntityCell,
  WhenCell,
} from "./auditShared";

const PAGE_SIZE = 50;

/** Convert a yyyy-MM-dd date input into an ISO bound (start/end of that day). */
function dateToIso(date: string | undefined, edge: "start" | "end"): string | undefined {
  if (!date) return undefined;
  const suffix = edge === "start" ? "T00:00:00.000Z" : "T23:59:59.999Z";
  const d = new Date(`${date}${suffix}`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

export function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const entityType = searchParams.get("entityType") ?? "";
  const entityId = searchParams.get("entityId") ?? undefined;
  const action = searchParams.get("action") ?? "";
  const actor = searchParams.get("actor") ?? "";
  const fromDate = searchParams.get("from") ?? "";
  const toDate = searchParams.get("to") ?? "";
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const offset = (page - 1) * PAGE_SIZE;

  const filters = useMemo(
    () => ({
      entityType: entityType || undefined,
      entityId,
      action: action || undefined,
      actorId: actor || undefined,
      from: dateToIso(fromDate || undefined, "start"),
      to: dateToIso(toDate || undefined, "end"),
      limit: PAGE_SIZE,
      offset,
    }),
    [entityType, entityId, action, actor, fromDate, toDate, offset],
  );

  const listQuery = useQuery({
    queryKey: auditKeys.list(filters),
    queryFn: () => auditService.list(filters),
  });

  function setParam(key: string, value: string | null) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      return next;
    });
  }

  const rows = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Batch firm-name resolution once for the whole page so ActorCell can render
  // the originating firm without each row calling the hook (no N+1).
  const companyIds = useMemo(
    () => rows.map(r => r.companyId).filter((id): id is string => Boolean(id)),
    [rows],
  );
  const companyNames = useCompanyNames(companyIds);

  const columns: Column<AuditEntry>[] = [
    {
      header: "When",
      accessor: "createdAt",
      render: (_v, row) => <WhenCell iso={row.createdAt} />,
    },
    {
      header: "Actor",
      accessor: "actor",
      render: (_v, row) => (
        <ActorCell
          actor={row.actor}
          companyName={row.companyId ? companyNames[row.companyId] : undefined}
        />
      ),
    },
    {
      header: "Entity",
      accessor: "entityType",
      render: (_v, row) => <EntityCell entry={row} />,
    },
    {
      header: "Action",
      accessor: "action",
      render: (_v, row) => <ActionPill action={row.action} />,
    },
  ];

  const entityFilterActive = Boolean(entityId);

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Audit log"
        subtitle="Reverse-chronological feed of profile changes across the platform."
      />

      <div className={styles.filters}>
        <div className={styles.filterCell}>
          <Select
            label="Entity type"
            value={entityType}
            options={ENTITY_TYPE_OPTIONS}
            onChange={e => setParam("entityType", e.target.value || null)}
            size="small"
          />
        </div>
        <div className={styles.filterCell}>
          <Select
            label="Action"
            value={action}
            options={ACTION_OPTIONS}
            onChange={e => setParam("action", e.target.value || null)}
            size="small"
          />
        </div>
        <div className={styles.filterCell}>
          <Input
            label="Actor"
            placeholder="Actor ID or email"
            value={actor}
            onChange={e => setParam("actor", e.target.value || null)}
          />
        </div>
        <div className={styles.filterCell}>
          <DatePicker
            label="From"
            value={fromDate}
            onChange={e => setParam("from", e.target.value || null)}
          />
        </div>
        <div className={styles.filterCell}>
          <DatePicker
            label="To"
            value={toDate}
            onChange={e => setParam("to", e.target.value || null)}
          />
        </div>
      </div>

      {entityFilterActive && (
        <p className={styles.muted}>
          Filtered to a single entity.{" "}
          <button
            type="button"
            className={styles.moreButton}
            onClick={() => setParam("entityId", null)}
          >
            Clear entity filter
          </button>
        </p>
      )}

      <div className={styles.contentSection}>
        {listQuery.isPending ? (
          <p role="status" className={styles.muted}>
            Loading audit log…
          </p>
        ) : listQuery.isError ? (
          <p role="alert" className={styles.errorBanner}>
            {listQuery.error instanceof Error
              ? listQuery.error.message
              : "Failed to load audit log"}
          </p>
        ) : rows.length === 0 ? (
          <p className={styles.emptyState}>No audit entries match your filters.</p>
        ) : (
          <>
            <DataTable data={rows} columns={columns} rowKey="id" />
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + rows.length}
              totalCount={total}
              itemName="audit entries"
              onPageChange={p => setParam("page", String(p))}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default AuditPage;
